'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { Calendar, CheckCircle2, Loader2, Clock, AlertCircle, Zap, Shield, BarChart3, RefreshCw, Receipt, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { sendPaymentReceiptEmail } from '@/lib/services/emailService';

interface Subscription {
  id: string;
  userId: string;
  plan: 'monthly' | 'yearly';
  status: 'active' | 'expired' | 'cancelled' | 'trial';
  amountInr: number;
  startedAt: string;
  expiresAt: string | null;
  createdAt: string;
}

interface PaymentRecord {
  id: string;
  plan: 'monthly' | 'yearly';
  amountInr: number;
  status: string;
  description: string;
  paidAt: string;
}

const PLANS = [
  {
    key: 'monthly' as const,
    label: 'Monthly',
    price: 1999,
    billingCycle: 'per month',
    description: 'Billed every 30 days. Cancel anytime.',
    features: ['Full project database access', 'Price trend analytics', 'Market comparison tools', 'PDF report exports', '20 project report downloads/month', '₹100 per additional download'],
    badge: null,
  },
  {
    key: 'yearly' as const,
    label: 'Yearly',
    price: 19999,
    billingCycle: 'per year',
    description: 'Billed annually. Save ₹23,989 vs monthly.',
    features: ['Everything in Monthly', 'Unlimited project report downloads', 'Priority data updates', 'Advanced micro-market filters', 'Dedicated support'],
    badge: 'Best Value',
  },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: 'Active', cls: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' },
    expired: { label: 'Expired', cls: 'bg-red-500/15 text-red-400 border border-red-500/30' },
    cancelled: { label: 'Cancelled', cls: 'bg-muted text-muted-foreground border border-border' },
    trial: { label: 'Trial', cls: 'bg-amber-500/15 text-amber-400 border border-amber-500/30' },
  };
  const s = map[status] ?? map.cancelled;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {s.label}
    </span>
  );
}

export default function SubscriptionPage() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState<string | null>(null);

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subError) {
        console.error('Subscription fetch error:', subError.message);
      } else if (subData) {
        setSubscription({
          id: subData.id,
          userId: subData.user_id,
          plan: subData.plan,
          status: subData.status,
          amountInr: subData.amount_inr,
          startedAt: subData.started_at,
          expiresAt: subData.expires_at,
          createdAt: subData.created_at,
        });
      }

      const { data: histData, error: histError } = await supabase
        .from('payment_history')
        .select('*')
        .eq('user_id', user.id)
        .order('paid_at', { ascending: false })
        .limit(10);

      if (histError) {
        console.error('Payment history fetch error:', histError.message);
      } else {
        setPaymentHistory(
          (histData || []).map((r) => ({
            id: r.id,
            plan: r.plan,
            amountInr: r.amount_inr,
            status: r.status,
            description: r.description,
            paidAt: r.paid_at,
          }))
        );
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      router.replace('/sign-up-login-screen');
    }
  }, [authLoading, user, router]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const handleRazorpayPayment = async (plan: 'monthly' | 'yearly') => {
    if (!user) return;
    setSwitching(plan);

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded || !(window as any).Razorpay) {
        toast.error('Payment gateway failed to load. Please check your connection and try again.');
        setSwitching(null);
        return;
      }

      const amount = plan === 'yearly' ? 19999 : 1999;

      // Create Razorpay order
      const orderRes = await fetch('/api/razorpay/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          currency: 'INR',
          receipt: `sub_${user.id.replace(/-/g, '').slice(0, 20)}_${Date.now().toString().slice(-8)}`,
          plan,
        }),
      });

      const orderData = await orderRes.json();
      if (!orderData.success) {
        toast.error(orderData.error || 'Failed to initiate payment. Please try again.');
        setSwitching(null);
        return;
      }

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'PropDataX',
        description: `${plan === 'yearly' ? 'Yearly' : 'Monthly'} Subscription`,
        order_id: orderData.orderId,
        prefill: {
          email: user.email || '',
        },
        theme: { color: '#f59e0b' },
        handler: async (response: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
          // Verify payment signature
          const verifyRes = await fetch('/api/razorpay/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(response),
          });
          const verifyData = await verifyRes.json();

          if (!verifyData.success) {
            toast.error('Payment verification failed. Please contact support.');
            setSwitching(null);
            return;
          }

          // Update subscription in Supabase
          await activateSubscription(plan, amount, verifyData.paymentId, verifyData.orderId);
        },
        modal: {
          ondismiss: () => {
            setSwitching(null);
          },
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', (res: any) => {
        toast.error(`Payment failed: ${res.error.description}`);
        setSwitching(null);
      });
      rzp.open();
    } catch (err) {
      console.error('Payment error:', err);
      toast.error('Something went wrong. Please try again.');
      setSwitching(null);
    }
  };

  const activateSubscription = async (
    plan: 'monthly' | 'yearly',
    amount: number,
    paymentId: string,
    orderId: string
  ) => {
    if (!user) return;

    const expiresAt = plan === 'yearly'
      ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

    try {
      if (subscription) {
        // Update existing subscription
        const { error: updateError } = await supabase
          .from('subscriptions')
          .update({
            plan,
            amount_inr: amount,
            status: 'active',
            expires_at: expiresAt,
            started_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', subscription.id)
          .eq('user_id', user.id);

        if (updateError) {
          toast.error('Payment received but subscription update failed. Contact support.');
          console.error('Subscription update error:', updateError.message);
          setSwitching(null);
          return;
        }

        // Log payment history
        await supabase.from('payment_history').insert({
          user_id: user.id,
          subscription_id: subscription.id,
          plan,
          amount_inr: amount,
          status: 'paid',
          description: `${plan === 'yearly' ? 'Yearly' : 'Monthly'} plan — Razorpay ${paymentId}`,
          paid_at: new Date().toISOString(),
        });
      } else {
        // Create new subscription
        const { data: newSub, error: insertError } = await supabase
          .from('subscriptions')
          .insert({
            user_id: user.id,
            plan,
            amount_inr: amount,
            status: 'active',
            expires_at: expiresAt,
            started_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (insertError) {
          toast.error('Payment received but subscription activation failed. Contact support.');
          console.error('Subscription insert error:', insertError.message);
          setSwitching(null);
          return;
        }

        // Log payment history
        await supabase.from('payment_history').insert({
          user_id: user.id,
          subscription_id: newSub.id,
          plan,
          amount_inr: amount,
          status: 'paid',
          description: `${plan === 'yearly' ? 'Yearly' : 'Monthly'} plan — Razorpay ${paymentId}`,
          paid_at: new Date().toISOString(),
        });
      }

      toast.success(`${plan === 'yearly' ? 'Yearly' : 'Monthly'} plan activated successfully!`);
      // Send payment receipt email
      if (user.email) {
        const renewalDate = new Date(expiresAt).toLocaleDateString('en-IN', {
          day: 'numeric', month: 'long', year: 'numeric',
        });
        sendPaymentReceiptEmail({
          to: user.email,
          fullName: user.user_metadata?.full_name || user.email,
          orderId,
          paymentId,
          plan,
          amountInr: amount,
          renewalDate,
        }).catch((err) => console.error('Receipt email failed:', err));
      }
      await fetchData();
    } catch (err) {
      console.error('Subscription activation error:', err);
      toast.error('Subscription activation failed. Please contact support.');
    } finally {
      setSwitching(null);
    }
  };

  if (authLoading || loading) {
    return (
      <AppLayout activeRoute="/subscription">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={28} className="animate-spin text-amber-400" />
        </div>
      </AppLayout>
    );
  }

  const currentPlan = PLANS.find((p) => p.key === subscription?.plan) ?? PLANS[0];
  const daysLeft = subscription?.expiresAt
    ? Math.max(0, Math.ceil((new Date(subscription.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <AppLayout activeRoute="/subscription">
      <div className="space-y-8 fade-in">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground tracking-tight">Subscription</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your plan, billing, and payment history.</p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all duration-150 border border-border"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>

        {/* Current Plan Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Plan Card */}
          <div className="card-base p-5 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <Zap size={16} className="text-amber-400" />
              </div>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Current Plan</span>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xl font-semibold text-foreground">{currentPlan.label}</span>
                {subscription && <StatusBadge status={subscription.status} />}
              </div>
              <div className="font-mono text-2xl font-bold text-amber-400">
                {formatAmount(currentPlan.price)}
                <span className="text-sm font-normal text-muted-foreground ml-1">{currentPlan.billingCycle}</span>
              </div>
              {subscription?.status === 'active' && daysLeft !== null && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock size={12} />
                  {daysLeft} days remaining
                </div>
              )}
            </div>
          </div>

          {/* Next Billing */}
          <div className="card-base p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
                <Calendar size={16} className="text-blue-400" />
              </div>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Next Billing</span>
            </div>
            <div className="space-y-1">
              <p className="text-lg font-semibold text-foreground">
                {subscription?.expiresAt ? formatDate(subscription.expiresAt) : '—'}
              </p>
              <p className="text-xs text-muted-foreground">
                {subscription?.status === 'active'
                  ? `Auto-renews at ${formatAmount(currentPlan.price)}`
                  : 'No active subscription'}
              </p>
            </div>
          </div>

          {/* Member Since */}
          <div className="card-base p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-teal/15 flex items-center justify-center">
                <Shield size={16} className="text-teal-400" />
              </div>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Member Since</span>
            </div>
            <div className="space-y-1">
              <p className="text-lg font-semibold text-foreground">
                {subscription?.startedAt ? formatDate(subscription.startedAt) : '—'}
              </p>
              <p className="text-xs text-muted-foreground">
                {subscription?.plan === 'yearly' ? 'Annual subscriber' : 'Monthly subscriber'}
              </p>
            </div>
          </div>
        </div>

        {/* Plan Selection */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-4">
            Available Plans
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PLANS.map((plan) => {
              const isCurrent = subscription?.plan === plan.key && subscription?.status === 'active';
              const isUpgrade = plan.key === 'yearly' && subscription?.plan === 'monthly';
              const isDowngrade = plan.key === 'monthly' && subscription?.plan === 'yearly';
              const isSwitching = switching === plan.key;

              return (
                <div
                  key={plan.key}
                  className={`card-base p-6 relative transition-all duration-200 ${
                    isCurrent
                      ? 'border-amber-500/40 bg-amber-500/5' :'hover:border-border/80'
                  }`}
                >
                  {plan.badge && !isCurrent && (
                    <span className="absolute top-4 right-4 bg-amber-500 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                      {plan.badge}
                    </span>
                  )}
                  {isCurrent && (
                    <span className="absolute top-4 right-4 flex items-center gap-1 text-amber-400 text-xs font-semibold">
                      <CheckCircle2 size={14} />
                      Current
                    </span>
                  )}

                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-foreground">{plan.label}</h3>
                    <div className="mt-1 font-mono text-2xl font-bold text-foreground">
                      {formatAmount(plan.price)}
                      <span className="text-sm font-normal text-muted-foreground ml-1">{plan.billingCycle}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium">
                      <CheckCircle2 size={15} />
                      Active Plan
                    </div>
                  ) : (
                    <button
                      onClick={() => handleRazorpayPayment(plan.key)}
                      disabled={isSwitching || switching !== null}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isUpgrade || !subscription
                          ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' :'bg-secondary hover:bg-muted border border-border text-foreground'
                      }`}
                    >
                      {isSwitching ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <CreditCard size={15} />
                      )}
                      {isSwitching
                        ? 'Processing...'
                        : !subscription
                        ? `Subscribe — ${formatAmount(plan.price)}`
                        : isUpgrade
                        ? `Upgrade to Yearly — ${formatAmount(plan.price)}`
                        : isDowngrade
                        ? `Switch to Monthly — ${formatAmount(plan.price)}`
                        : `Subscribe — ${formatAmount(plan.price)}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Powered by Razorpay badge */}
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Shield size={12} className="text-emerald-400" />
            Payments secured by Razorpay. Supports cards, UPI, net banking & wallets.
          </div>

          {/* Download Limits Info */}
          <div className="mt-5 card-base p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-sky-500/15 flex items-center justify-center">
                <BarChart3 size={14} className="text-sky-400" />
              </div>
              <h3 className="text-sm font-semibold text-foreground">Project Report Download Limits</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/30 border border-border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield size={14} className="text-amber-400" />
                  <span className="text-xs font-semibold text-foreground">Monthly Plan</span>
                </div>
                <ul className="space-y-1.5">
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 size={11} className="text-emerald-400 shrink-0 mt-0.5" />
                    20 project report downloads per month (free)
                  </li>
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <AlertCircle size={11} className="text-amber-400 shrink-0 mt-0.5" />
                    ₹100 charged per additional download beyond 20
                  </li>
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 size={11} className="text-emerald-400 shrink-0 mt-0.5" />
                    Limit resets on the 1st of every month
                  </li>
                </ul>
              </div>
              <div className="bg-muted/30 border border-amber-500/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap size={14} className="text-amber-400" />
                  <span className="text-xs font-semibold text-foreground">Yearly Plan</span>
                </div>
                <ul className="space-y-1.5">
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 size={11} className="text-emerald-400 shrink-0 mt-0.5" />
                    Unlimited project report downloads
                  </li>
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 size={11} className="text-emerald-400 shrink-0 mt-0.5" />
                    No additional charges ever
                  </li>
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 size={11} className="text-emerald-400 shrink-0 mt-0.5" />
                    Download as many reports as you need
                  </li>
                </ul>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              Download limits and additional charges are clearly shown on the Reports page before you download. Each download generates a consolidated Excel file with project overview, pricing analysis, and absorption data.
            </p>
          </div>
        </div>

        {/* No subscription state */}
        {!subscription && (
          <div className="card-base p-8 text-center">
            <AlertCircle size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground">No active subscription found</p>
            <p className="text-xs text-muted-foreground mt-1">Choose a plan above to get started.</p>
          </div>
        )}

        {/* Payment History */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Receipt size={16} className="text-muted-foreground" />
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest">Payment History</h2>
          </div>

          {paymentHistory.length === 0 ? (
            <div className="card-base p-8 text-center">
              <BarChart3 size={28} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No payment records yet.</p>
            </div>
          ) : (
            <div className="card-base overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-widest">Date</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-widest">Description</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-widest">Plan</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-widest">Amount</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-widest">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((record, idx) => (
                      <tr
                        key={record.id}
                        className={`border-b border-border/50 hover:bg-muted/30 transition-colors duration-100 ${
                          idx === paymentHistory.length - 1 ? 'border-b-0' : ''
                        }`}
                      >
                        <td className="px-5 py-3.5 text-muted-foreground font-mono text-xs whitespace-nowrap">
                          {formatDate(record.paidAt)}
                        </td>
                        <td className="px-5 py-3.5 text-foreground max-w-xs truncate">
                          {record.description || `${record.plan} plan payment`}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground capitalize">
                            {record.plan}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right font-mono font-semibold text-foreground whitespace-nowrap">
                          {formatAmount(record.amountInr)}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium capitalize ${
                              record.status === 'paid' ?'bg-emerald-500/15 text-emerald-400' :'bg-muted text-muted-foreground'
                            }`}
                          >
                            {record.status === 'paid' && <CheckCircle2 size={10} />}
                            {record.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
